import { analysisApi, type AIAnalysisRequestParams } from '@/api/precision-new/analysis';
import type {
  AIAnalysisResult,
  AIThinkingCache,
  AIThinkingContent,
  AnalysisProgress,
  AnalysisStage,
  AnalysisStatus,
  ComponentState,
  ErrorInfo,
  SSEMessageData,
} from '@/types/precision-new/ai-analysis';
import { computed, nextTick, reactive, readonly, ref } from 'vue';

export function useAIAnalysis() {
  // 基础状态
  const status = ref<AnalysisStatus>('idle');
  const progress = ref(0);
  const stage = ref<AnalysisStage>('data_collection');
  const message = ref('');
  const error = ref('');
  const analysisResult = ref<AIAnalysisResult | null>(null);

  // AI思考缓存
  const aiThinkingCache = reactive<AIThinkingCache>({
    fullContent: '',
    displayContent: '',
    chunks: [],
    startTime: null,
    endTime: null,
    currentChunkCount: 0,
    lastDisplayedLength: 0,
  });

  // 控制对象
  let analysisController: { cancel: () => void } | null = null;

  // 计算属性
  const isAnalyzing = computed(() => status.value === 'analyzing');
  const isCompleted = computed(() => status.value === 'completed');
  const isError = computed(() => status.value === 'error');
  const hasResult = computed(() => !!analysisResult.value);

  // 思考过程相关
  const hasThinkingContent = computed(() => !!aiThinkingCache.fullContent);
  const thinkingDuration = computed(() => {
    if (!aiThinkingCache.startTime) {
      return '--';
    }
    const endTime = aiThinkingCache.endTime || new Date();
    const durationMs = endTime.getTime() - aiThinkingCache.startTime.getTime();
    const seconds = Math.round(durationMs / 1000);
    return `${Math.floor(seconds / 60)}分${seconds % 60}秒`;
  });

  // 统计数据
  const statistics = computed(() => {
    if (!analysisResult.value) {
      return {
        totalFiles: '-',
        totalTestCases: '-',
        recommendedTests: '-',
        coverageRate: '-',
      };
    }

    const { data_collection_info, summary, minimal_test_set, coverage_estimation } = analysisResult.value;

    return {
      totalFiles: data_collection_info?.total_changed_files || summary?.total_changed_files || 0,
      totalTestCases: data_collection_info?.total_test_cases || summary?.total_test_cases || 0,
      recommendedTests: minimal_test_set?.length || summary?.recommended_test_cases || 0,
      coverageRate: coverage_estimation?.estimated_coverage
        ? `${Math.round(coverage_estimation.estimated_coverage * 100)}%`
        : summary?.estimated_coverage
        ? `${Math.round(summary.estimated_coverage * 100)}%`
        : '-',
    };
  });

  // 组件状态
  const componentState = computed<ComponentState>(() => ({
    status: status.value,
    progress: progress.value,
    stage: stage.value,
    message: message.value,
    error: error.value,
  }));

  // 重置状态
  function resetState() {
    status.value = 'idle';
    progress.value = 0;
    stage.value = 'data_collection';
    message.value = '';
    error.value = '';
    analysisResult.value = null;

    // 重置AI思考缓存
    Object.assign(aiThinkingCache, {
      fullContent: '',
      displayContent: '',
      chunks: [],
      startTime: null,
      endTime: null,
      currentChunkCount: 0,
      lastDisplayedLength: 0,
    });

    // 取消之前的分析
    if (analysisController) {
      analysisController.cancel();
      analysisController = null;
    }
  }

  // 获取阶段显示名称
  function getStageDisplayName(currentStage: AnalysisStage): string {
    const stageNames = {
      'data_collection': '收集数据中...',
      'git_api': '获取代码变更...',
      'database': '查询测试用例...',
      'ai_analysis': 'AI智能分析中...',
      'ai_thinking': 'AI思考分析中...',
      'ai_calling': 'AI调用中...',
      'processing_stream': '处理AI响应...',
      'parsing_result': '解析分析结果...',
      'finalizing': '生成最终报告...',
      'completed': '分析完成',
      'error': '分析失败',
    };
    return stageNames[currentStage] || currentStage;
  }

  // 处理进度更新
  function handleProgress(progressData: AnalysisProgress) {
    stage.value = progressData.stage;
    progress.value = Math.round(progressData.progress * 100);
    message.value = progressData.message || getStageDisplayName(progressData.stage);
  }

  // 处理AI思考内容
  function handleAIThinking(content: AIThinkingContent) {
    if (!aiThinkingCache.startTime) {
      aiThinkingCache.startTime = new Date();
    }

    if (content.chunk_count) {
      aiThinkingCache.currentChunkCount = content.chunk_count;
    }

    if (content.incremental_content) {
      if (!aiThinkingCache.fullContent) {
        aiThinkingCache.fullContent = content.incremental_content;
      } else {
        aiThinkingCache.fullContent += content.incremental_content;
      }

      // 更新显示内容
      if (!aiThinkingCache.displayContent) {
        aiThinkingCache.displayContent = '';
      }
      aiThinkingCache.displayContent += content.incremental_content;

      // 记录chunk信息
      aiThinkingCache.chunks.push({
        timestamp: new Date(),
        chunkCount: content.chunk_count || 0,
        contentLength: content.incremental_content.length,
        isFinalBatch: content.is_final_batch || false,
      });

      // 限制chunks数组长度
      if (aiThinkingCache.chunks.length > 50) {
        aiThinkingCache.chunks = aiThinkingCache.chunks.slice(-50);
      }
    }

    if (content.recent_content && !content.incremental_content) {
      aiThinkingCache.fullContent = content.recent_content;
    }
  }

  // 处理AI内容数据
  function handleAIContent(contentData: { content: string }) {
    if (contentData.content) {
      const currentLength = aiThinkingCache.fullContent?.length || 0;
      const newLength = contentData.content.length;

      if (newLength > currentLength || currentLength === 0) {
        aiThinkingCache.fullContent = contentData.content;
      }
    }
  }

  // 处理分析结果
  function handleResult(result: AIAnalysisResult) {
    analysisResult.value = result;
    status.value = 'completed';

    // 标记AI思考完成
    if (!aiThinkingCache.endTime) {
      aiThinkingCache.endTime = new Date();
    }
  }

  // 处理错误
  function handleError(errorInfo: ErrorInfo | Error | string) {
    status.value = 'error';

    if (typeof errorInfo === 'string') {
      error.value = errorInfo;
    } else if (errorInfo instanceof Error) {
      error.value = errorInfo.message;
    } else {
      error.value = errorInfo.message;
      if (errorInfo.stage) {
        stage.value = errorInfo.stage;
      }
    }
  }

  // 处理SSE消息
  function handleSSEMessage(data: SSEMessageData) {
    switch (data.type) {
      case 'progress':
        handleProgress(data.content as AnalysisProgress);
        break;
      case 'ai_thinking':
        handleAIThinking(data.content as AIThinkingContent);
        break;
      case 'ai_content':
        handleAIContent(data.content as { content: string });
        break;
      case 'debug_info':
        // 处理调试信息，如果有last_500_chars则当作AI内容处理
        const debugInfo = data.content as any;
        if (debugInfo.last_500_chars) {
          handleAIContent({ content: debugInfo.last_500_chars });
        }
        break;
      case 'result':
        handleResult(data.content as AIAnalysisResult);
        break;
      case 'error':
        handleError(data.content as ErrorInfo);
        break;
      default:
        // 忽略未知的消息类型
        console.warn('Unknown SSE message type:', data.type);
        break;
    }
  }

  // 开始AI分析
  async function startAnalysis(params: AIAnalysisRequestParams) {
    resetState();
    status.value = 'analyzing';

    try {
      analysisController = analysisApi.startAIAnalysisStreaming(
        params,
        {
          onMessage: handleSSEMessage,
          onError: handleError,
          onComplete: () => {
            if (status.value === 'analyzing') {
              // 如果还在分析状态，说明可能是网络中断
              handleError('分析连接中断');
            }
          },
          onOpen: () => {
            console.log('AI分析SSE连接已建立');
          },
        },
        {
          // 使用 Vite 代理，无超时限制
          // timeout: 15 * 60 * 1000,
        },
      );
    } catch (err) {
      handleError(err as Error);
    }
  }

  // 取消分析
  function cancelAnalysis() {
    if (analysisController) {
      analysisController.cancel();
      analysisController = null;
    }
    status.value = 'idle';
  }

  // 重试分析
  function retryAnalysis(params: AIAnalysisRequestParams) {
    resetState();
    nextTick(() => {
      startAnalysis(params);
    });
  }

  return {
    // 状态
    status: readonly(status),
    progress: readonly(progress),
    stage: readonly(stage),
    message: readonly(message),
    error: readonly(error),
    analysisResult: readonly(analysisResult),
    aiThinkingCache: readonly(aiThinkingCache),

    // 计算属性
    isAnalyzing,
    isCompleted,
    isError,
    hasResult,
    hasThinkingContent,
    thinkingDuration,
    statistics,
    componentState,

    // 方法
    startAnalysis,
    cancelAnalysis,
    retryAnalysis,
    resetState,
    getStageDisplayName,
  };
}
